import { atom, computed, onSet, task } from 'nanostores'

import listDealers from '@/services/listDealers'
import type { Dealer } from '@/services/schemas'
import { $countries } from '@/stores/Countries'
import { $country } from '@/stores/Country'

const $dealers = atom<Dealer[]>([])
const $isLoadingDealers = atom<boolean>(false)
const $selectedState = atom<string>('')
// Tracks whether dealers have been fetched for the current country/state
const $dealersFetched = atom<boolean>(false)

// Computed store to determine if dealer functionality should be available
// Returns true only when dealers have been fetched AND there are dealers available
const $hasDealersAvailable = computed(
  [$dealers, $dealersFetched, $isLoadingDealers],
  (dealers, fetched, loading) => {
    // If still loading or not yet fetched, we don't know yet - return null to indicate "unknown"
    if (loading || !fetched) return null
    return dealers.length > 0
  }
)

// Fetch dealers for a given country and optional state
const fetchDealersForCountry = async (countryCode: string, state?: string) => {
  $isLoadingDealers.set(true)
  $dealersFetched.set(false)

  try {
    const dealersData = await listDealers(countryCode, state || undefined)
    $dealers.set(dealersData)
  }
  catch (error) {
    console.error('Failed to fetch dealers:', error)
    $dealers.set([])
  }
  finally {
    $isLoadingDealers.set(false)
    $dealersFetched.set(true)
  }
}

// Check if a country has state selection
const countryHasStateSelection = (countryCode: string): boolean => {
  const countries = $countries.get()
  const country = countries.find(c => c.isocode2 === countryCode)
  return country?.has_state_selection === '1'
}

// Fetch dealers when country changes
onSet($country, ({ newValue }) => {
  if (!newValue) {
    $dealers.set([])
    $selectedState.set('')
    $isLoadingDealers.set(false)
    $dealersFetched.set(false)
    return
  }

  // Reset selected state and dealers when country changes
  $selectedState.set('')
  $dealers.set([])
  $dealersFetched.set(false)

  // Check if country has state selection
  // If not, fetch dealers immediately
  // If yes, wait for user to select a state
  if (!countryHasStateSelection(newValue)) {
    task(() => fetchDealersForCountry(newValue))
  } else {
    // Country has state selection, so we're not loading dealers yet
    // (waiting for state selection)
    $isLoadingDealers.set(false)
  }
})

// Fetch dealers when selected state changes
onSet($selectedState, ({ newValue }) => {
  const countryCode = $country.get()
  if (!countryCode) {
    $dealers.set([])
    return
  }

  // Only fetch if a state is actually selected
  if (newValue) {
    task(() => fetchDealersForCountry(countryCode, newValue))
  }
})

// Handle the case when countries are loaded with a preselected country
// that doesn't have state selection (initial load case)
onSet($countries, ({ newValue }) => {
  const countryCode = $country.get()
  if (!countryCode || newValue.length === 0) return

  const country = newValue.find(c => c.isocode2 === countryCode)
  
  // If country has no state selection and dealers haven't been loaded yet, fetch them
  if (country?.has_state_selection !== '1' && $dealers.get().length === 0 && !$isLoadingDealers.get()) {
    task(() => fetchDealersForCountry(countryCode))
  }
})

export {
  $dealers,
  $dealersFetched,
  $hasDealersAvailable,
  $isLoadingDealers,
  $selectedState,
}
